import { CheckCircleOutlined, MessageOutlined, SafetyCertificateOutlined, StopOutlined, TeamOutlined } from '@ant-design/icons';
import { Callout, HelpParagraph, HeroBlock, KeyValueList, SectionTitle, StepFlow } from '@/features/help/HelpPrimitives';

// Help guide for Agent Studio → Platform → Action policies (topic 'governance.action-policies').

export const ActionPoliciesHelpBody = () => (
  <>
    <HeroBlock
      icon={<SafetyCertificateOutlined />}
      title="Action policies"
      tagline="Some things an agent can do write to the record or tell other people — create a task, book an appointment, file a symptom. This page decides, per practice, which of those the agent may do on its own and which must be approved by a person first."
      accent="violet"
    />

    <HelpParagraph>
      Every row is an action the code knows how to take. A rule can be set once for every practice (the
      platform default) and overridden for one practice. With no rule at all, an action runs on its own,
      exactly as it always has — so turning a rule on is the only way behaviour changes.
    </HelpParagraph>

    <SectionTitle>The five choices</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Runs on its own', value: 'The agent does it when asked.' },
        { key: 'Runs, then tells the person', value: 'Meant to run with a notice and an undo window. The notice and undo are not built yet, so today it simply runs.' },
        { key: 'Asks the person in the chat first', value: 'The chat stops on an approval card showing exactly what will be written. Nothing happens until the person taps Approve. Voice and older apps that cannot show the card refuse the action instead.' },
        { key: 'Needs a named role to approve', value: 'For a chat action: refused with a sentence today. For a clinical request (anticoagulation, abnormal lab): sent to that role’s decisions queue with clocks.' },
        { key: 'Never allowed', value: 'Refused every time.' },
      ]}
    />

    <SectionTitle>How a change takes effect</SectionTitle>
    <StepFlow
      steps={[
        { icon: <MessageOutlined />, title: 'Save', body: 'The rule applies from the next request — no deploy. Saving a change raises the version.' },
        { icon: <SafetyCertificateOutlined />, title: 'Sign', body: 'An admin signs the version they reviewed. Changing a signed rule clears the signature, so a signature always covers exactly what was approved.', accent: 'violet' },
        { icon: <CheckCircleOutlined />, title: 'Unsigned still applies', body: 'Signing records who approved the rule; it does not switch it on. The page lists unsigned rules so none is forgotten.', accent: 'green' },
      ]}
    />

    <Callout tone="warning" title="The platform default reaches every practice">
      A platform rule applies to every practice that has no rule of its own. To change one practice only,
      pick it in the header and save its own rule.
    </Callout>

    <SectionTitle>Who can change it</SectionTitle>
    <HelpParagraph>
      <TeamOutlined /> Anyone in Agent Studio can read this page. Only an admin account can save or sign.
      <StopOutlined style={{ marginLeft: 8 }} /> Switching a rule off (rather than deleting it) keeps its history.
    </HelpParagraph>
  </>
);
